import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { userService } from '../api/userService';
import { X, User, Briefcase, Calendar, Search, Download, ChevronDown, ChevronRight } from 'lucide-react';
import { getBioLink } from '../utils/bioLinks';
import { ORGANOGRAM_HIERARCHY } from '../constants/divisions';

const UserDrillDown = () => {
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('drilldown'); // 'drilldown' or 'matrix'

    const { data: usersData, isLoading: loading, error: queryError } = useQuery({
        queryKey: ['users'],
        queryFn: async () => {
            const data = await userService.getAllUsers();
            return Array.isArray(data) ? data : (data.Users || []);
        },
        staleTime: 300000
    });

    const users = usersData || [];
    const error = queryError ? "Failed to load members. Please ensure the API is available." : null;

    const selectedUserId = selectedUser ? (selectedUser.UserId || selectedUser.Username || selectedUser.sub || selectedUser.id) : null;

    const { data: userDetails, isLoading: loadingDetails, error: detailsError } = useQuery({
        queryKey: ['user_details', selectedUserId],
        queryFn: () => userService.getUserDetail(selectedUserId),
        enabled: !!selectedUserId,
        staleTime: 60000
    });

    const handleUserClick = (user) => {
        setSelectedUser(user);
    };

    const closeModal = () => {
        setSelectedUser(null);
    };

    const filteredUsers = users.filter(user => {
        const term = searchTerm.toLowerCase();
        const name = (user.Name || user.Username || '').toLowerCase();
        const email = (user.Email || '').toLowerCase();
        const group = (user.Division || user.Attributes?.find(a => a.Name === 'custom:division')?.Value || '').toLowerCase();
        return name.includes(term) || email.includes(term) || group.includes(term);
    });

    return (
        <div style={{ height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', padding: '0.75rem' }}>
            <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <h2 style={{ margin: '0 0 0.25rem 0', fontSize: '1.25rem' }}>Team Member History & Skills</h2>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.85rem' }}>Select a team member to view their detailed history or explore the Skills Matrix mapping.</p>
                </div>
                
                {/* View Switcher */}
                <div style={{ display: 'flex', gap: '0.5rem', background: '#f1f5f9', padding: '4px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <button
                        onClick={() => setViewMode('drilldown')}
                        style={{
                            background: viewMode === 'drilldown' ? 'white' : 'transparent',
                            boxShadow: viewMode === 'drilldown' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.4rem 1rem',
                            fontWeight: 600,
                            color: viewMode === 'drilldown' ? '#1e293b' : '#64748b',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            fontSize: '0.85rem'
                        }}
                    >
                        History & Performance
                    </button>
                    <button
                        onClick={() => setViewMode('matrix')}
                        style={{
                            background: viewMode === 'matrix' ? 'white' : 'transparent',
                            boxShadow: viewMode === 'matrix' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.4rem 1rem',
                            fontWeight: 600,
                            color: viewMode === 'matrix' ? '#1e293b' : '#64748b',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            fontSize: '0.85rem'
                        }}
                    >
                        Skills Matrix Mapping
                    </button>
                </div>
            </div>

            {viewMode === 'drilldown' ? (
                <div style={{ flex: 1, display: 'flex', gap: '0.75rem', overflow: 'hidden' }}>
                {/* Left Panel: User List */}
                <div className="modern-card" style={{
                    flex: '0 0 280px', display: 'flex', flexDirection: 'column',
                    overflow: 'hidden', padding: 0
                }}>
                    <div style={{ padding: '0.75rem', borderBottom: '1px solid #eee', background: '#f8f9fa' }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>Members ({filteredUsers.length})</h4>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="modern-input"
                            style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.85rem', padding: '0.4rem' }}
                        />
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', padding: '0.25rem' }}>
                        {loading && <p style={{ padding: '0.5rem', fontSize: '0.85rem' }}>Loading Team Members...</p>}
                        {error && <p style={{ color: 'red', padding: '0.5rem', fontSize: '0.85rem' }}>{error}</p>}

                        {!loading && !error && filteredUsers.map((user, index) => (
                            <div
                                key={index}
                                onClick={() => handleUserClick(user)}
                                style={{
                                    padding: '0.5rem', borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                                    background: selectedUser === user ? '#e3f2fd' : 'transparent',
                                    borderRadius: '4px', marginBottom: '0.1rem',
                                    transition: 'background 0.2s'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <div style={{
                                        width: '32px', height: '32px', borderRadius: '50%', overflow: 'hidden',
                                        background: user.Attributes?.find(a => a.Name === 'picture')?.Value ? 'transparent' : '#e9ecef',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        {user.Attributes?.find(a => a.Name === 'picture')?.Value ? (
                                            <img
                                                src={user.Attributes.find(a => a.Name === 'picture').Value}
                                                alt={user.Name || user.Username || 'User'}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <User size={14} color="#495057" />
                                        )}
                                    </div>
                                    <div style={{ overflow: 'hidden', flex: 1 }}>
                                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#2c3e50', marginBottom: '0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {user.Name || user.Username || 'Unknown User'}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#555', marginBottom: '0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {user.Email || 'No Email'}
                                        </div>
                                        <div style={{ fontSize: '0.7rem', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {user.Profile && <span style={{ marginRight: '6px', color: '#4a90e2' }}>{user.Profile}</span>}
                                            <span style={{ color: '#aaa' }}>|</span>
                                            <span style={{ marginLeft: '6px' }}>{user.Division || user.Attributes?.find(a => a.Name === 'custom:division')?.Value || 'No Group'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right Panel: Details */}
                <div className="modern-card" style={{
                    flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0
                }}>
                    {selectedUser ? (

                        <>
                            <div style={{
                                padding: '1rem',
                                background: 'linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)',
                                color: 'white',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <div style={{
                                        width: '48px', height: '48px', borderRadius: '50%', overflow: 'hidden',
                                        background: selectedUser.Attributes?.find(a => a.Name === 'picture')?.Value ? 'transparent' : 'rgba(255,255,255,0.2)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                    }}>
                                        {selectedUser.Attributes?.find(a => a.Name === 'picture')?.Value ? (
                                            <img
                                                src={selectedUser.Attributes.find(a => a.Name === 'picture').Value}
                                                alt={selectedUser.Name || selectedUser.Username || 'User'}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <User size={24} color="white" />
                                        )}
                                    </div>
                                    <div>
                                        <h3 style={{ margin: '0 0 0.1rem 0', fontSize: '1.25rem', fontWeight: 'bold', color: 'white', display: 'flex', alignItems: 'center' }}>
                                            <a 
                                                href={getBioLink(selectedUser.Name || selectedUser.Username)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: 'white', textDecoration: 'none' }}
                                                onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                                                onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                                                title={`View ${selectedUser.Name || selectedUser.Username}'s profile on RIS website`}
                                            >
                                                {selectedUser.Name || selectedUser.Username}
                                            </a>
                                            {userDetails?.HighestQualification && <span style={{ marginLeft: '12px', background: '#3b82f6', color: 'white', fontSize: '0.8rem', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>{userDetails.HighestQualification}</span>}
                                        </h3>

                                        {/* Categories horizontal layout */}
                                        {userDetails && (userDetails.ThematicSpecialization?.length > 0 || userDetails.AreaSpecialization?.length > 0 || userDetails.SpecialSkills?.length > 0) ? (
                                            <table style={{ marginTop: '0.75rem', borderCollapse: 'collapse' }}>
                                                <tbody>
                                                    <tr>
                                                        {userDetails.ThematicSpecialization?.length > 0 && (
                                                            <td style={{ verticalAlign: 'top', paddingRight: '1.5rem', borderRight: '1px solid rgba(255,255,255,0.1)' }}>
                                                                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#93c5fd', textTransform: 'uppercase', marginBottom: '0.4rem', letterSpacing: '0.05em' }}>Thematic</div>
                                                                <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', maxWidth: '180px' }}>
                                                                    {userDetails.ThematicSpecialization.map((tag, i) => <span key={i} style={{ background: '#e0f2fe', color: '#0369a1', padding: '1px 6px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: 500 }}>{tag}</span>)}
                                                                </div>
                                                            </td>
                                                        )}
                                                        {userDetails.AreaSpecialization?.length > 0 && (
                                                            <td style={{ verticalAlign: 'top', paddingRight: '1.5rem', paddingLeft: userDetails.ThematicSpecialization?.length > 0 ? '1.5rem' : '0', borderRight: userDetails.SpecialSkills?.length > 0 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                                                                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6ee7b7', textTransform: 'uppercase', marginBottom: '0.4rem', letterSpacing: '0.05em' }}>Area</div>
                                                                <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', maxWidth: '180px' }}>
                                                                    {userDetails.AreaSpecialization.map((tag, i) => <span key={i} style={{ background: '#ecfdf5', color: '#047857', padding: '1px 6px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: 500 }}>{tag}</span>)}
                                                                </div>
                                                            </td>
                                                        )}
                                                        {userDetails.SpecialSkills?.length > 0 && (
                                                            <td style={{ verticalAlign: 'top', paddingLeft: (userDetails.ThematicSpecialization?.length > 0 || userDetails.AreaSpecialization?.length > 0) ? '1.5rem' : '0' }}>
                                                                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#fcd34d', textTransform: 'uppercase', marginBottom: '0.4rem', letterSpacing: '0.05em' }}>Special</div>
                                                                <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', maxWidth: '180px' }}>
                                                                    {userDetails.SpecialSkills.map((tag, i) => <span key={i} style={{ background: '#fef3c7', color: '#b45309', padding: '1px 6px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: 500 }}>{tag}</span>)}
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                </tbody>
                                            </table>
                                        ) : (
                                            <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.9)' }}>
                                                {selectedUser.Email || selectedUser.Attributes?.find(a => a.Name === 'email')?.Value}
                                            </div>
                                        )}

                                        <div style={{ fontSize: '0.75rem', marginTop: '0.25rem', display: 'flex', gap: '0.75rem', color: 'rgba(255,255,255,0.8)' }}>
                                            {selectedUser.Profile && <span>Profile: {selectedUser.Profile}</span>}
                                            <span>Division: {selectedUser.Division || selectedUser.Attributes?.find(a => a.Name === 'custom:division')?.Value || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                                <button onClick={closeModal} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', color: 'white', padding: '0.4rem', borderRadius: '50%' }} title="Close">
                                    <X size={16} />
                                </button>
                            </div>

                            <div style={{ flex: 1, overflowY: 'auto', padding: '1rem', background: '#fafafa' }}>
                                {loadingDetails ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem', fontSize: '0.9rem' }}>Loading history...</div>
                                ) : detailsError ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem', fontSize: '0.9rem', color: 'red' }}>Failed to load user history.</div>
                                ) : userDetails ? (
                                    <div>
                                        {/* Summary Cards */}
                                        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
                                            <SummaryCard title="Tasks Created" value={userDetails.summary?.openTasks + userDetails.summary?.completedTasks || 0} icon={<Briefcase size={16} />} color="#007bff" />
                                            <SummaryCard title="Tasks Completed" value={userDetails.summary?.completedTasks || 0} icon={<Briefcase size={16} />} color="#28a745" />
                                            <SummaryCard title="Events" value={userDetails.summary?.openEvents + userDetails.summary?.completedEvents || 0} icon={<Calendar size={16} />} color="#17a2b8" />
                                        </div>

                                        {/* Recent Tasks List */}
                                        <div className="modern-card" style={{ padding: '0.75rem', marginBottom: '1rem', borderRadius: '8px', border: '1px solid #eef2f6' }}>
                                            <h4 style={{ marginTop: 0, fontSize: '0.95rem', marginBottom: '0.5rem' }}>Recent Tasks</h4>
                                            {userDetails.tasks?.length > 0 ? (
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                                    <thead>
                                                        <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                                                            <th style={{ padding: '0.35rem', textAlign: 'left', color: '#666' }}>Title</th>
                                                            <th style={{ padding: '0.35rem', textAlign: 'left', color: '#666' }}>Status</th>
                                                            <th style={{ padding: '0.35rem', textAlign: 'left', color: '#666' }}>Date</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {userDetails.tasks.map(task => (
                                                            <tr key={task.TaskID} style={{ borderBottom: '1px solid #f9f9f9' }}>
                                                                <td style={{ padding: '0.35rem' }}>{task.Title}</td>
                                                                <td style={{ padding: '0.35rem' }}>
                                                                    <span style={{
                                                                        padding: '0.1rem 0.4rem', borderRadius: '12px', fontSize: '0.7rem',
                                                                        background: task.Status === 'Completed' ? '#d4edda' : '#fff3cd',
                                                                        color: task.Status === 'Completed' ? '#155724' : '#856404'
                                                                    }}>
                                                                        {task.Status}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '0.35rem', color: '#888' }}>{task.CreatedAt?.split('T')[0]}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : <p style={{ color: '#999', fontStyle: 'italic', fontSize: '0.85rem' }}>No tasks found.</p>}
                                        </div>

                                        {/* Recent Activity List (Aggregated) */}
                                        <div className="modern-card" style={{ padding: '0.75rem', marginBottom: '1rem', borderRadius: '8px', border: '1px solid #eef2f6' }}>
                                            <h4 style={{ marginTop: 0, fontSize: '0.95rem', marginBottom: '0.5rem' }}>Recent Activity</h4>
                                            {(() => {
                                                // Aggregate all items
                                                const allActivity = [
                                                    ...(userDetails.tasks || []).map(t => ({ ...t, Type: 'Task', ID: t.TaskID, details: t.Priority })),
                                                    ...(userDetails.subtasks || []).map(t => ({ 
                                                        ...t, 
                                                        Type: 'Subtask', 
                                                        ID: t.SubTaskID,
                                                        Title: t.Title || t.ActivityNameManual || t.ActivityType || 'Untitled Subtask'
                                                    })),
                                                    ...(userDetails.events || []).map(e => ({ ...e, Type: 'Event', ID: e.EventID, details: e.EventType })),
                                                    ...(userDetails.subevents || []).map(e => ({ ...e, Type: 'Subevent', ID: e.SubEventID }))
                                                ];

                                                // Sort by CreatedAt Descending
                                                allActivity.sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt));

                                                // Take top 20
                                                const recentActivity = allActivity.slice(0, 20);

                                                if (recentActivity.length === 0) return <p style={{ color: '#999', fontStyle: 'italic', fontSize: '0.85rem' }}>No recent activity found.</p>;

                                                return (
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                                        <thead>
                                                            <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                                                                 <th style={{ padding: '0.35rem', textAlign: 'left', color: '#666' }}>Type</th>
                                                                 <th style={{ padding: '0.35rem', textAlign: 'left', color: '#666' }}>Title</th>
                                                                 <th style={{ padding: '0.35rem', textAlign: 'left', color: '#666' }}>Status</th>
                                                                 <th style={{ padding: '0.35rem', textAlign: 'left', color: '#666' }}>Date</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {recentActivity.map(item => (
                                                                <tr key={`${item.Type}-${item.ID}`} style={{ borderBottom: '1px solid #f9f9f9' }}>
                                                                    <td style={{ padding: '0.35rem' }}>
                                                                        <span style={{
                                                                            fontSize: '0.7rem', fontWeight: 600, padding: '1px 5px', borderRadius: '4px',
                                                                            background: item.Type === 'Task' ? '#e3f2fd' :
                                                                                item.Type === 'Event' ? '#f3e8ff' :
                                                                                    item.Type === 'Subtask' ? '#fff3e0' : '#f1f5f9',
                                                                            color: item.Type === 'Task' ? '#1565c0' :
                                                                                item.Type === 'Event' ? '#7c3aed' :
                                                                                    item.Type === 'Subtask' ? '#e65100' : '#475569'
                                                                        }}>
                                                                            {item.Type}
                                                                        </span>
                                                                    </td>
                                                                    <td style={{ padding: '0.35rem' }}>
                                                                        <div style={{ fontWeight: 500, color: '#333', fontSize: '0.85rem' }}>{item.Title || 'Untitled'}</div>
                                                                        {item.ParentTitle && (
                                                                            <div style={{ fontSize: '0.75rem', color: '#4b5563', marginTop: '0.15rem', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                                                                                <span style={{ color: '#9ca3af', fontWeight: 500 }}>Part of:</span>
                                                                                <span style={{ fontWeight: 500, color: '#1f2937' }}>{item.ParentTitle}</span>
                                                                            </div>
                                                                        )}
                                                                        {item.details && <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '0.1rem' }}>{item.details}</div>}
                                                                    </td>
                                                                    <td style={{ padding: '0.35rem' }}>
                                                                        <span style={{
                                                                            fontSize: '0.75rem', fontWeight: 600,
                                                                            color: item.Status === 'Completed' || item.Status === 'Scheduled' ? '#16a34a' :
                                                                                item.Status === 'Cancelled' ? '#dc2626' : '#d97706'
                                                                        }}>
                                                                            {item.Status || 'Open'}
                                                                        </span>
                                                                    </td>
                                                                    <td style={{ padding: '0.35rem', color: '#888' }}>
                                                                        {item.CreatedAt ? item.CreatedAt.split('T')[0] : '-'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>No details available for this team member.</div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                            <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '50%', marginBottom: '0.5rem' }}>
                                <User size={32} color="#dee2e6" />
                            </div>
                            <h3 style={{ fontSize: '1.1rem', margin: '0.5rem 0' }}>Select a Team Member</h3>
                            <p style={{ fontSize: '0.85rem' }}>Click on a team member from the list to view their history.</p>
                        </div>
                    )}
                </div>
            </div>
            ) : (
                <SkillsMatrix users={users} setSelectedUser={setSelectedUser} setViewMode={setViewMode} />
            )}
        </div>
    );
};

// Skills Matrix Mapping subcomponent
const SkillsMatrix = ({ users, setSelectedUser, setViewMode }) => {
    const [skillSearch, setSkillSearch] = useState('');
    const [employeeSearch, setEmployeeSearch] = useState('');
    const [selectedSkillTypes, setSelectedSkillTypes] = useState({
        thematic: true,
        area: true,
        special: true
    });
    const [expandedSections, setExpandedSections] = useState({
        thematic: true,
        area: true,
        special: true
    });

    const PILLARS = [
        'Development Cooperation',
        'SDGs-Life',
        'Trade and Investment',
        'Maritime Economy & Connectivity',
        'Finance',
        'Health',
        'Science and Technology',
        'Agriculture',
        'RIS Central / Admin'
    ];

    // Explicit robust mapping for all division names/codes from the database / CSV
    const DIVISION_TO_PILLAR_MAP = {
        'CONNECTIVITY': 'Maritime Economy & Connectivity',
        'SDG': 'SDGs-Life',
        'SDGS-LIFE': 'SDGs-Life',
        'FINANCE': 'Finance',
        'AIC': 'Trade and Investment',
        'ASEAN-INDIA CENTRE': 'Trade and Investment',
        'SCIENCE DIPLOMACY': 'Science and Technology',
        'SCIENCE': 'Science and Technology',
        'DEVELOPMENT COOPERATION': 'Development Cooperation',
        'DC': 'Development Cooperation',
        'BLUE ECONOMY': 'Maritime Economy & Connectivity',
        'BE': 'Maritime Economy & Connectivity',
        'FITM': 'Health',
        'CMEC': 'Maritime Economy & Connectivity',
        'AGRICULTURE': 'Agriculture',
        'AGRI': 'Agriculture',
        'STANDARD': 'Trade and Investment',
        'STD': 'Trade and Investment',
        'DAKSHIN': 'Development Cooperation',
        'TRADE INVESTMENT WTO': 'Trade and Investment',
        'TIWTO': 'Trade and Investment',
        'CENTRAL RIS': 'RIS Central / Admin',
        'CENTRAL RIS ': 'RIS Central / Admin',
        'RIS': 'RIS Central / Admin',
        'DG OFFICE': 'RIS Central / Admin',
        'PUBLICATION AND MEDIA': 'RIS Central / Admin',
        'PUB': 'RIS Central / Admin',
        'IT': 'RIS Central / Admin',
        'ADMIN': 'RIS Central / Admin'
    };

    // Helper: Map user to pillar
    const getPillarForUser = (user) => {
        const div = (user.Division || '').toUpperCase().trim();
        if (!div) return 'RIS Central / Admin';

        // 1. Check explicit robust mapping dictionary
        if (DIVISION_TO_PILLAR_MAP[div]) {
            return DIVISION_TO_PILLAR_MAP[div];
        }

        // 2. Fall back to matching standard area IDs/parentDivIds in organogram hierarchy
        for (const pillar of ORGANOGRAM_HIERARCHY) {
            const match = pillar.areas.some(area => {
                const areaId = area.id.toUpperCase();
                const parentDivId = (area.parentDivId || '').toUpperCase();
                return div === areaId || div === parentDivId;
            });
            if (match) return pillar.name;
        }

        return 'RIS Central / Admin';
    };

    // Extract unique skills
    const skillData = useMemo(() => {
        const thematic = new Set();
        const area = new Set();
        const special = new Set();

        users.forEach(u => {
            if (Array.isArray(u.ThematicSpecialization)) {
                u.ThematicSpecialization.forEach(s => {
                    const clean = s.trim();
                    if (clean) thematic.add(clean);
                });
            }
            if (Array.isArray(u.AreaSpecialization)) {
                u.AreaSpecialization.forEach(s => {
                    const clean = s.trim();
                    if (clean) area.add(clean);
                });
            }
            if (Array.isArray(u.SpecialSkills)) {
                u.SpecialSkills.forEach(s => {
                    const clean = s.trim();
                    if (clean) special.add(clean);
                });
            }
        });

        return {
            thematic: Array.from(thematic).sort(),
            area: Array.from(area).sort(),
            special: Array.from(special).sort()
        };
    }, [users]);

    // Find users with specific skill under specific pillar
    const getMatchingUsers = (skillName, skillType, pillarName) => {
        return users.filter(u => {
            // Check pillar match
            if (getPillarForUser(u) !== pillarName) return false;

            // Check skill match
            if (skillType === 'thematic') {
                return (u.ThematicSpecialization || []).some(s => s.trim() === skillName);
            } else if (skillType === 'area') {
                return (u.AreaSpecialization || []).some(s => s.trim() === skillName);
            } else if (skillType === 'special') {
                return (u.SpecialSkills || []).some(s => s.trim() === skillName);
            }
            return false;
        });
    };

    // Export to CSV Function
    const handleExportCSV = () => {
        let csvContent = "data:text/csv;charset=utf-8,";
        // Header
        csvContent += `Skill Category,Skill Name,${PILLARS.join(",")}\n`;

        // Rows
        const appendRows = (skillsList, typeLabel) => {
            skillsList.forEach(skill => {
                let row = `"${typeLabel}","${skill.replace(/"/g, '""')}"`;
                PILLARS.forEach(pillar => {
                    const matches = getMatchingUsers(skill, typeLabel.toLowerCase().replace(" skills", ""), pillar);
                    const namesStr = matches.map(m => m.Name || m.Username).join("; ");
                    row += `,"${namesStr.replace(/"/g, '""')}"`;
                });
                csvContent += row + "\n";
            });
        };

        if (selectedSkillTypes.thematic) appendRows(skillData.thematic, "Thematic");
        if (selectedSkillTypes.area) appendRows(skillData.area, "Area");
        if (selectedSkillTypes.special) appendRows(skillData.special, "Special");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `RIS_Skills_Matrix_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Filter skill names
    const filteredThematic = skillData.thematic.filter(s => s.toLowerCase().includes(skillSearch.toLowerCase()));
    const filteredArea = skillData.area.filter(s => s.toLowerCase().includes(skillSearch.toLowerCase()));
    const filteredSpecial = skillData.special.filter(s => s.toLowerCase().includes(skillSearch.toLowerCase()));

    // Toggle Section
    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Control Bar */}
            <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '0.75rem 1rem',
                marginBottom: '0.75rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
            }}>
                <div style={{ display: 'flex', gap: '0.75rem', flex: 1, minWidth: '300px' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                        <input
                            type="text"
                            placeholder="Filter skills..."
                            value={skillSearch}
                            onChange={(e) => setSkillSearch(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.4rem 0.8rem 0.4rem 2.2rem',
                                borderRadius: '8px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.8rem',
                                outline: 'none',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                        <input
                            type="text"
                            placeholder="Highlight employee..."
                            value={employeeSearch}
                            onChange={(e) => setEmployeeSearch(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.4rem 0.8rem 0.4rem 2.2rem',
                                borderRadius: '8px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.8rem',
                                outline: 'none',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.8rem', color: '#475569', fontWeight: 500 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={selectedSkillTypes.thematic}
                                onChange={(e) => setSelectedSkillTypes(prev => ({ ...prev, thematic: e.target.checked }))}
                            />
                            Thematic
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={selectedSkillTypes.area}
                                onChange={(e) => setSelectedSkillTypes(prev => ({ ...prev, area: e.target.checked }))}
                            />
                            Area
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={selectedSkillTypes.special}
                                onChange={(e) => setSelectedSkillTypes(prev => ({ ...prev, special: e.target.checked }))}
                            />
                            Special
                        </label>
                    </div>

                    <button
                        onClick={handleExportCSV}
                        style={{
                            background: '#0ea5e9',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.4rem 0.8rem',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'background 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#0284c7'}
                        onMouseLeave={e => e.currentTarget.style.background = '#0ea5e9'}
                    >
                        <Download size={14} />
                        Export CSV
                    </button>
                </div>
            </div>

            {/* Matrix Table container */}
            <div style={{
                flex: 1,
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                overflow: 'auto',
                background: 'white',
                position: 'relative'
            }}>
                <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: '1200px' }}>
                    <thead>
                        <tr>
                            {/* Sticky Left Corner Header */}
                            <th style={{
                                position: 'sticky',
                                left: 0,
                                top: 0,
                                background: '#f1f5f9',
                                zIndex: 10,
                                borderBottom: '2px solid #cbd5e1',
                                borderRight: '2px solid #cbd5e1',
                                padding: '0.75rem',
                                textAlign: 'left',
                                fontSize: '0.8rem',
                                color: '#1e293b',
                                fontWeight: 700,
                                width: '220px',
                                minWidth: '220px',
                                boxSizing: 'border-box'
                            }}>
                                Skill Category / Name
                            </th>
                            {PILLARS.map(pillar => (
                                <th key={pillar} style={{
                                    position: 'sticky',
                                    top: 0,
                                    background: '#f1f5f9',
                                    zIndex: 5,
                                    borderBottom: '2px solid #cbd5e1',
                                    borderRight: '1px solid #e2e8f0',
                                    padding: '0.75rem 0.5rem',
                                    textAlign: 'center',
                                    fontSize: '0.75rem',
                                    color: '#1e293b',
                                    fontWeight: 700,
                                    width: '130px',
                                    minWidth: '130px',
                                    boxSizing: 'border-box'
                                }}>
                                    {pillar}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {/* THEMATIC SKILLS SECTION */}
                        {selectedSkillTypes.thematic && (
                            <>
                                <tr style={{ background: '#f8fafc', cursor: 'pointer' }} onClick={() => toggleSection('thematic')}>
                                    <td
                                        colSpan={PILLARS.length + 1}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            fontWeight: 700,
                                            fontSize: '0.8rem',
                                            color: '#0369a1',
                                            borderBottom: '1px solid #e2e8f0',
                                            borderRight: '2px solid #cbd5e1',
                                            background: '#f0f9ff',
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 2,
                                            textAlign: 'left'
                                        }}
                                    >
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                            {expandedSections.thematic ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                            THEMATIC SKILLS ({filteredThematic.length})
                                        </span>
                                    </td>
                                </tr>
                                {expandedSections.thematic && filteredThematic.map((skill, index) => (
                                    <tr key={`thematic-${skill}`} style={{ background: index % 2 === 0 ? '#ffffff' : '#fcfcfc' }}>
                                        <td style={{
                                            position: 'sticky',
                                            left: 0,
                                            background: index % 2 === 0 ? '#ffffff' : '#fcfcfc',
                                            zIndex: 2,
                                            borderBottom: '1px solid #e2e8f0',
                                            borderRight: '2px solid #cbd5e1',
                                            padding: '0.6rem 0.8rem',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: '#334155'
                                        }}>
                                            {skill}
                                        </td>
                                        {PILLARS.map(pillar => {
                                            const matches = getMatchingUsers(skill, 'thematic', pillar);
                                            return (
                                                <td key={`thematic-${skill}-${pillar}`} style={{
                                                    borderBottom: '1px solid #e2e8f0',
                                                    borderRight: '1px solid #e2e8f0',
                                                    padding: '0.4rem',
                                                    verticalAlign: 'middle',
                                                    textAlign: 'center'
                                                }}>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
                                                        {matches.map(emp => {
                                                            const isSearched = employeeSearch && emp.Name?.toLowerCase().includes(employeeSearch.toLowerCase());
                                                            const isFaded = employeeSearch && !isSearched;
                                                            return (
                                                                <span
                                                                    key={emp.UserId}
                                                                    onClick={() => {
                                                                        setSelectedUser(emp);
                                                                        setViewMode('drilldown');
                                                                    }}
                                                                    style={{
                                                                        background: isSearched ? '#fef08a' : '#e0f2fe',
                                                                        color: isSearched ? '#854d0e' : '#0369a1',
                                                                        border: isSearched ? '2px solid #ca8a04' : '1px solid transparent',
                                                                        opacity: isFaded ? 0.35 : 1,
                                                                        padding: '2px 8px',
                                                                        borderRadius: '12px',
                                                                        fontSize: '0.7rem',
                                                                        fontWeight: 600,
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s',
                                                                        boxShadow: isSearched ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                                                                    }}
                                                                    title={`${emp.Name || emp.Username} (${emp.Division || 'No Division'})\nClick to view history`}
                                                                >
                                                                    {emp.Name || emp.Username}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </>
                        )}

                        {/* AREA SKILLS SECTION */}
                        {selectedSkillTypes.area && (
                            <>
                                <tr style={{ background: '#f8fafc', cursor: 'pointer' }} onClick={() => toggleSection('area')}>
                                    <td
                                        colSpan={PILLARS.length + 1}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            fontWeight: 700,
                                            fontSize: '0.8rem',
                                            color: '#065f46',
                                            borderBottom: '1px solid #e2e8f0',
                                            borderRight: '2px solid #cbd5e1',
                                            background: '#ecfdf5',
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 2,
                                            textAlign: 'left'
                                        }}
                                    >
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                            {expandedSections.area ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                            AREA SPECIALIZATIONS ({filteredArea.length})
                                        </span>
                                    </td>
                                </tr>
                                {expandedSections.area && filteredArea.map((skill, index) => (
                                    <tr key={`area-${skill}`} style={{ background: index % 2 === 0 ? '#ffffff' : '#fcfcfc' }}>
                                        <td style={{
                                            position: 'sticky',
                                            left: 0,
                                            background: index % 2 === 0 ? '#ffffff' : '#fcfcfc',
                                            zIndex: 2,
                                            borderBottom: '1px solid #e2e8f0',
                                            borderRight: '2px solid #cbd5e1',
                                            padding: '0.6rem 0.8rem',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: '#334155'
                                        }}>
                                            {skill}
                                        </td>
                                        {PILLARS.map(pillar => {
                                            const matches = getMatchingUsers(skill, 'area', pillar);
                                            return (
                                                <td key={`area-${skill}-${pillar}`} style={{
                                                    borderBottom: '1px solid #e2e8f0',
                                                    borderRight: '1px solid #e2e8f0',
                                                    padding: '0.4rem',
                                                    verticalAlign: 'middle',
                                                    textAlign: 'center'
                                                }}>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
                                                        {matches.map(emp => {
                                                            const isSearched = employeeSearch && emp.Name?.toLowerCase().includes(employeeSearch.toLowerCase());
                                                            const isFaded = employeeSearch && !isSearched;
                                                            return (
                                                                <span
                                                                    key={emp.UserId}
                                                                    onClick={() => {
                                                                        setSelectedUser(emp);
                                                                        setViewMode('drilldown');
                                                                    }}
                                                                    style={{
                                                                        background: isSearched ? '#fef08a' : '#d1fae5',
                                                                        color: isSearched ? '#854d0e' : '#065f46',
                                                                        border: isSearched ? '2px solid #ca8a04' : '1px solid transparent',
                                                                        opacity: isFaded ? 0.35 : 1,
                                                                        padding: '2px 8px',
                                                                        borderRadius: '12px',
                                                                        fontSize: '0.7rem',
                                                                        fontWeight: 600,
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s',
                                                                        boxShadow: isSearched ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                                                                    }}
                                                                    title={`${emp.Name || emp.Username} (${emp.Division || 'No Division'})\nClick to view history`}
                                                                >
                                                                    {emp.Name || emp.Username}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </>
                        )}

                        {/* SPECIAL SKILLS SECTION */}
                        {selectedSkillTypes.special && (
                            <>
                                <tr style={{ background: '#f8fafc', cursor: 'pointer' }} onClick={() => toggleSection('special')}>
                                    <td
                                        colSpan={PILLARS.length + 1}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            fontWeight: 700,
                                            fontSize: '0.8rem',
                                            color: '#92400e',
                                            borderBottom: '1px solid #e2e8f0',
                                            borderRight: '2px solid #cbd5e1',
                                            background: '#fef3c7',
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 2,
                                            textAlign: 'left'
                                        }}
                                    >
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                            {expandedSections.special ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                            SPECIAL SKILLS ({filteredSpecial.length})
                                        </span>
                                    </td>
                                </tr>
                                {expandedSections.special && filteredSpecial.map((skill, index) => (
                                    <tr key={`special-${skill}`} style={{ background: index % 2 === 0 ? '#ffffff' : '#fcfcfc' }}>
                                        <td style={{
                                            position: 'sticky',
                                            left: 0,
                                            background: index % 2 === 0 ? '#ffffff' : '#fcfcfc',
                                            zIndex: 2,
                                            borderBottom: '1px solid #e2e8f0',
                                            borderRight: '2px solid #cbd5e1',
                                            padding: '0.6rem 0.8rem',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: '#334155'
                                        }}>
                                            {skill}
                                        </td>
                                        {PILLARS.map(pillar => {
                                            const matches = getMatchingUsers(skill, 'special', pillar);
                                            return (
                                                <td key={`special-${skill}-${pillar}`} style={{
                                                    borderBottom: '1px solid #e2e8f0',
                                                    borderRight: '1px solid #e2e8f0',
                                                    padding: '0.4rem',
                                                    verticalAlign: 'middle',
                                                    textAlign: 'center'
                                                }}>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
                                                        {matches.map(emp => {
                                                            const isSearched = employeeSearch && emp.Name?.toLowerCase().includes(employeeSearch.toLowerCase());
                                                            const isFaded = employeeSearch && !isSearched;
                                                            return (
                                                                <span
                                                                    key={emp.UserId}
                                                                    onClick={() => {
                                                                        setSelectedUser(emp);
                                                                        setViewMode('drilldown');
                                                                    }}
                                                                    style={{
                                                                        background: isSearched ? '#fef08a' : '#fef3c7',
                                                                        color: isSearched ? '#854d0e' : '#b45309',
                                                                        border: isSearched ? '2px solid #ca8a04' : '1px solid transparent',
                                                                        opacity: isFaded ? 0.35 : 1,
                                                                        padding: '2px 8px',
                                                                        borderRadius: '12px',
                                                                        fontSize: '0.7rem',
                                                                        fontWeight: 600,
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s',
                                                                        boxShadow: isSearched ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                                                                    }}
                                                                    title={`${emp.Name || emp.Username} (${emp.Division || 'No Division'})\nClick to view history`}
                                                                >
                                                                    {emp.Name || emp.Username}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const SummaryCard = ({ title, value, icon, color }) => (
    <div className="modern-card" style={{
        flex: 1, minWidth: '120px', display: 'flex', alignItems: 'center', gap: '0.75rem',
        background: 'white', border: 'none', borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '1rem'
    }}>
        <div style={{ padding: '0.5rem', borderRadius: '50%', background: `${color}15`, color: color }}>
            {icon}
        </div>
        <div>
            <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.1rem', fontWeight: 500 }}>{title}</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#202124' }}>{value}</div>
        </div>
    </div>
);

export default UserDrillDown;
